import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  skip,
  take,
  tap,
} from 'rxjs/operators';
import { I2gMoney } from './i2g-money.data';
import {
  getEditingSettings,
  getI2gMoney,
  getOriginalEditingSettings,
  getPayPalOnboardingUrl,
  getProcessing,
  getSettings,
  getSettingsDispatchedResponse,
  getUnderwritingUrl,
  initialState,
  ISettingsDispatchedResponse,
} from './settings-data.reducer';
import { Settings } from './settings.model';
import { TaxYearStart } from './sub-models/company-settings/tax-year-start.model';

@Injectable()
export class SettingsDataStore {
  private _originalEditingSettings$: Observable<Settings> = this._store.pipe(
    select(getOriginalEditingSettings)
  );

  settings$: Observable<Settings> = this._store.pipe(
    select(getSettings),
    filter((settings: Settings) => settings !== initialState.settings)
  );
  editingSettings$: Observable<Settings> = this._store.pipe(
    select(getEditingSettings)
  );

  i2gMoney$: Observable<I2gMoney> = this._store.pipe(
    select(getI2gMoney),
    filter((i2gMoney: I2gMoney) => !isEqual(i2gMoney, initialState.i2gMoney))
  );

  constructor(private _store: Store<any>) {}

  getSettings(): Observable<Settings> {
    return this.settings$.pipe(take(1));
  }

  getTaxYearStartChanges(): Observable<TaxYearStart> {
    return this.settings$.pipe(
      filter((x) => !!x),
      skip(1),
      map((settings: Settings) => settings.companySettings.taxYearStart),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
  }

  getEditingSettings(skipFirst: boolean = false): Observable<Settings> {
    return this.editingSettings$.pipe(
      filter((es) => !!es),
      skip(+skipFirst),
      take(1)
    );
  }

  getOriginalEditingSettings(): Observable<Settings> {
    return this._originalEditingSettings$.pipe(take(1));
  }

  getUnderwritingUrl(): Observable<string> {
    return this._store.pipe(select(getUnderwritingUrl));
  }

  getPayPalOnboardingUrl(): Observable<string> {
    return this._store.pipe(select(getPayPalOnboardingUrl));
  }

  getSettingsDispatchedResponse(dispatchId: string): Observable<any> {
    return this._store.pipe(
      select(getSettingsDispatchedResponse),
      filter(
        (response: ISettingsDispatchedResponse) =>
          response && response.dispatchId === dispatchId
      ),
      tap((response: ISettingsDispatchedResponse) => {
        if (response.error) {
          throw response.error;
        }
      }),
      map((response: ISettingsDispatchedResponse) => response.entity),
      take(1)
    );
  }

  getI2gMoney(): Observable<I2gMoney> {
    return this.i2gMoney$;
  }

  getProcessing(): Observable<boolean> {
    return this._store.pipe(
      select(getProcessing),
      filter((processing) => processing !== initialState.processing)
    );
  }
}
